/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, memo, useCallback, useEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import noop from '@atlaskit/ds-lib/noop';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import { token } from '@atlaskit/tokens';

import type { TabIndex } from '../../types';
import type { DateObj } from '../types';

const dateCellSiblingStyle = css({
	color: token('color.text.subtlest'),
	transition: token('motion.listitem.hovered'),
	'&:hover': {
		color: token('color.text.subtlest'),
	},
});

const dateCellTodayStyle = css({
	color: token('color.text.selected'),
	fontWeight: token('font.weight.bold'),
	'&::after': {
		display: 'block',
		height: 2,
		position: 'absolute',
		backgroundColor: 'currentColor',
		content: '""',
		insetBlockEnd: token('space.025'),
		insetInlineEnd: token('space.025'),
		insetInlineStart: token('space.025'),
	},
});

const dateCellPrevSelectedStyle = css({
	backgroundColor: token('color.background.selected'),
	color: token('color.text.subtle'),
	transition: token('motion.listitem.hovered'),
	'&:hover': {
		color: token('color.text.subtle'),
	},
});

const dateCellSelectedStyle = css({
	backgroundColor: token('color.background.selected'),
	color: token('color.text.selected'),
	transition: token('motion.listitem.hovered'),
	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
		color: token('color.text.selected'),
	},
});

// platform-dst-tokens-finesse cleanup: merge into dateCellSelectedStyle after rollout
const dateCellSelectedFinesseStyle = css({
	backgroundColor: token('color.background.selected.bold'),
	color: token('color.text.inverse'),
	transition: token('motion.listitem.hovered'),
	'&:hover': {
		backgroundColor: token('color.background.selected.bold.hovered'),
		color: token('color.text.inverse'),
	},
	'&:active': {
		backgroundColor: token('color.background.selected.bold.pressed'),
		color: token('color.text.inverse'),
		transition: token('motion.listitem.pressed'),
	},
});

const dateCellDisabledStyle = css({
	color: token('color.text.disabled'),
	cursor: 'not-allowed',
	transition: token('motion.listitem.hovered'),
	'&:hover': {
		backgroundColor: 'transparent',
		color: token('color.text.disabled'),
	},
});

const dateCellStyles = css({
	all: 'unset',
	display: 'block',
	position: 'relative',
	flexGrow: 1,
	backgroundColor: 'transparent',
	borderColor: 'transparent',
	borderRadius: token('radius.medium'),
	borderStyle: 'solid',
	borderWidth: token('border.width.selected'),
	color: token('color.text'),
	cursor: 'pointer',
	font: token('font.body'),
	paddingBlockEnd: token('space.050'),
	paddingBlockStart: token('space.050'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingInlineEnd: '9px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingInlineStart: '9px',
	textAlign: 'center',
	transition: token('motion.listitem.hovered'),
	'&:focus-visible': {
		borderColor: token('color.border.focused'),
		borderStyle: 'solid',
		borderWidth: token('border.width.focused'),
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		color: token('color.text'),
	},
	'&:active': {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
		color: token('color.text'),
		transition: token('motion.listitem.pressed'),
	},
});

const dateCellMotionStyles = css({
	transition: token('motion.listitem.hovered'),
	'&:hover': {
		transition: token('motion.listitem.hovered'),
	},
	'&:active': {
		transition: token('motion.listitem.pressed'),
	},
	// Focus rings must stay immediate — non-negotiable per the motion decision record.
	'&:focus-visible': {
		transition: 'none',
	},
});

const dateCellSelectedMotionStyles = css({
	transition: token('motion.listitem.selected'),
});

interface DateProps {
	children: number;
	isDisabled?: boolean;
	isFocused?: boolean;
	isToday?: boolean;
	dayLong: string;
	month: number;
	monthLong: string;
	onClick?: ({ day, month, year }: DateObj) => void;
	isPreviouslySelected?: boolean;
	isSelected?: boolean;
	isSibling?: boolean;
	year: number;
	shouldSetFocus: boolean;
	tabIndex: TabIndex;
	testId?: string;
}

const Date: import('react').MemoExoticComponent<
	import('react').ForwardRefExoticComponent<
		DateProps & import('react').RefAttributes<HTMLButtonElement>
	>
> = memo(
	forwardRef<HTMLButtonElement, DateProps>(
		(
			{
				children: day,
				isDisabled = false,
				isFocused = false,
				isToday = false,
				dayLong,
				month,
				monthLong,
				onClick = noop,
				isPreviouslySelected = false,
				isSelected = false,
				isSibling = false,
				year,
				shouldSetFocus,
				tabIndex,
				testId,
			},
			_ref,
		) => {
			const dateRef = useRef({ day, month, year, isDisabled });

			useEffect(() => {
				dateRef.current = {
					day,
					month,
					year,
					isDisabled,
				};
			}, [day, month, year, isDisabled]);

			const focusRef = useRef(null);

			useEffect(() => {
				if (isFocused && shouldSetFocus && focusRef.current) {
					(focusRef.current as HTMLButtonElement).focus();
				}
			}, [isFocused, shouldSetFocus]);

			const handleClick = useCallback(() => {
				const {
					day: dayValue,
					month: monthValue,
					year: yearValue,
					isDisabled: isDisabledValue,
				} = dateRef.current;

				if (!isDisabledValue) {
					onClick({
						day: dayValue,
						month: monthValue,
						year: yearValue,
					});
				}
			}, [onClick]);

			return (
				<Grid role="gridcell" alignItems="center">
					<button
						css={[
							dateCellStyles,
							isSibling && dateCellSiblingStyle,
							isToday && dateCellTodayStyle,
							isPreviouslySelected && dateCellPrevSelectedStyle,
							isSelected && dateCellSelectedStyle,
							isSelected && fg('platform-dst-tokens-finesse') && dateCellSelectedFinesseStyle,
							isDisabled && dateCellDisabledStyle,
							dateCellMotionStyles,
							(isSelected || isPreviouslySelected) && dateCellSelectedMotionStyles,
						]}
						aria-current={isToday ? 'date' : undefined}
						aria-disabled={isDisabled || undefined}
						aria-label={`${day}, ${dayLong} ${monthLong} ${year}`}
						aria-pressed={isSelected ? 'true' : 'false'}
						tabIndex={isFocused ? tabIndex : -1}
						type="button"
						onClick={handleClick}
						ref={focusRef}
						data-disabled={isDisabled || undefined}
						data-focused={isFocused || undefined}
						data-prev-selected={isPreviouslySelected || undefined}
						data-selected={isSelected || undefined}
						data-sibling={isSibling || undefined}
						data-today={isToday || undefined}
						data-testid={testId && (isSelected ? `${testId}--selected-day` : `${testId}--day`)}
					>
						{day}
					</button>
				</Grid>
			);
		},
	),
);

Date.displayName = 'Date';

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default Date;
